/**
 * Mapping and diffing for a merged channel's Control write routing rules.
 *
 * Rules are their own REST resource (`/control_routes/`), not a field of the
 * channel, so saving a channel has to reconcile the edited rule set against the
 * one loaded when the editor opened: create what is new, update what changed,
 * delete what the operator removed. That reconciliation is pure and lives here
 * so it can be unit-tested without a store, a form or a network.
 *
 * Endpoint ids come from the server's `channel_endpoints`, never from the
 * channel form's local endpoint rows. A rule target is a foreign key; pointing
 * it at an id the client invented would dangle the moment the server assigned
 * the real one.
 */
import { ControlFanOut } from "../../../flatbuffers/generated/archive/control-fan-out";
import { Control } from "../../../flatbuffers/generated/control/control";
import type { ControlRouteRow, RoutableEndpoint } from "../components/ControlRouteTable";

/** One target as LiftCloud returns it on a route. */
interface ServerTarget {
  endpoint_id?: string;
  endpointId?: string;
  ordinal?: number;
}

/** A route as LiftCloud returns it from GET /control_routes/. */
export interface ServerControlRoute {
  id?: string;
  control?: number;
  fan_out?: number;
  fanOut?: number;
  precedence?: number;
  active?: boolean;
  targets?: ServerTarget[];
}

/** A channel endpoint as LiftCloud returns it from GET /channels/:id/endpoints. */
export interface ServerChannelEndpoint {
  id?: string;
  address?: string;
  group_id?: number;
  groupId?: number;
  priority?: number;
  control_writable?: boolean;
  controlWritable?: boolean;
}

/** Body shape of POST/PUT /control_routes/ (`ControlRouteRequest`). */
export interface ControlRouteRequestBody {
  id?: string;
  channel_id: string;
  control: number;
  fan_out: number;
  precedence: number;
  active: boolean;
  targets: { endpoint_id: string; ordinal: number; active: boolean }[];
}

/** What `handleUpdateChannel` has to send after the channel itself is saved. */
export interface ControlRouteDiff {
  create: ControlRouteRequestBody[];
  update: ControlRouteRequestBody[];
  deleteIds: string[];
}

/**
 * Server endpoints → the options the routing table offers.
 *
 * A read-only leg (`control_writable: false`) stays in the list rather than
 * being filtered out here: the table needs it to explain why an existing rule
 * targeting it is invalid. It filters the *selectable* options itself.
 */
export function toRoutableEndpoints(endpoints: ServerChannelEndpoint[]): RoutableEndpoint[] {
  return endpoints
    .filter((e) => Boolean(e.id))
    .map((e) => {
      const group = e.group_id ?? e.groupId;
      const suffix = group === undefined ? "" : ` (group ${group})`;
      return {
        id: String(e.id),
        label: `${e.address ?? "endpoint"}${suffix}`,
        controlWritable: (e.control_writable ?? e.controlWritable ?? true) !== false,
      };
    });
}

/** Server routes → editor rows, targets kept in ordinal order. */
export function toControlRouteRows(routes: ServerControlRoute[]): ControlRouteRow[] {
  return routes
    .filter((r) => r.active !== false)
    .map((r) => ({
      key: String(r.id ?? `${r.control ?? 0}-${r.precedence ?? 0}`),
      control: (r.control ?? 0) as Control,
      fanOut: (r.fan_out ?? r.fanOut ?? ControlFanOut.SINGLE) as ControlFanOut,
      precedence: r.precedence ?? 0,
      targetEndpointIds: [...(r.targets ?? [])]
        .sort((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0))
        .map((t) => String(t.endpoint_id ?? t.endpointId ?? ""))
        .filter((id) => id !== ""),
    }))
    .sort((a, b) => a.precedence - b.precedence);
}

function toBody(row: ControlRouteRow, channelId: string): ControlRouteRequestBody {
  return {
    channel_id: channelId,
    control: row.control,
    fan_out: row.fanOut,
    precedence: row.precedence,
    active: true,
    // Position in the list IS the fallback order under SINGLE fan-out, so the
    // index is the ordinal rather than an arbitrary sequence.
    targets: row.targetEndpointIds.map((id, ordinal) => ({
      endpoint_id: id,
      ordinal,
      active: true,
    })),
  };
}

/** True when two rows describe the same rule, so an unchanged row is not PUT. */
function rowsEqual(a: ControlRouteRow, b: ControlRouteRow): boolean {
  return (
    a.control === b.control &&
    a.fanOut === b.fanOut &&
    a.precedence === b.precedence &&
    a.targetEndpointIds.length === b.targetEndpointIds.length &&
    a.targetEndpointIds.every((id, i) => id === b.targetEndpointIds[i])
  );
}

/**
 * Reconciles the edited rule set against the one loaded when the editor opened.
 *
 * A row's `key` is the server id for rules that came from the server, and a
 * locally-minted uuid for ones the operator just added — that is what separates
 * a create from an update. Rows whose fields are unchanged are omitted from
 * `update` so saving a channel after editing only its IP issues no route writes
 * at all.
 */
export function diffControlRoutes(
  original: ControlRouteRow[],
  current: ControlRouteRow[],
  channelId: string,
): ControlRouteDiff {
  const originalByKey = new Map(original.map((r) => [r.key, r]));
  const currentKeys = new Set(current.map((r) => r.key));

  const create: ControlRouteRequestBody[] = [];
  const update: ControlRouteRequestBody[] = [];

  current.forEach((row) => {
    const before = originalByKey.get(row.key);
    if (before === undefined) {
      create.push(toBody(row, channelId));
      return;
    }
    if (!rowsEqual(before, row)) {
      update.push({ ...toBody(row, channelId), id: row.key });
    }
  });

  const deleteIds = original.filter((r) => !currentKeys.has(r.key)).map((r) => r.key);

  return { create, update, deleteIds };
}
